"use client"

/**
 * CountListCell — hiển thị một quan hệ nhiều-mục (danh mục, website, thẻ…) dưới
 * dạng pill đếm số ("2 items ▾"). Click vào pill để mở Popover liệt kê đầy đủ
 * các mục dưới dạng chip, thay vì nối chuỗi tên bị cắt cụt trong ô bảng.
 *
 * Dùng token admin (--admin-*) để hợp cả light/dark. Rỗng → em-dash muted.
 */

import { t } from "@/languages/i18n"
import { DownOutlined } from "@ant-design/icons"
import { Popover } from "antd"

interface CountListCellProps {
  /** Nhãn của từng mục (đã lọc rỗng bởi caller là tốt nhất). */
  items: Array<string | null | undefined>
  /** Danh từ đơn/số nhiều cạnh số đếm. Mặc định "item"/"items". */
  noun?: { singular: string; plural: string }
  /** Tiêu đề tùy chọn phía trên danh sách trong Popover. */
  title?: string
}

export function CountListCell({ items, noun, title }: CountListCellProps) {
  const labels = items.map(item => (item == null ? "" : String(item))).filter(Boolean)
  const count = labels.length

  if (count === 0) {
    return <span style={{ color: "var(--admin-muted-fg)" }}>—</span>
  }

  const singular = noun?.singular ?? t("item")
  const plural = noun?.plural ?? t("items")
  const nounLabel = count === 1 ? singular : plural

  const content = (
    <div
      style={{ display: "flex", flexWrap: "wrap", gap: 6, maxWidth: 320 }}
      onClick={e => e.stopPropagation()}
    >
      {labels.map((label, index) => (
        <span
          key={`${label}-${index}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 10px",
            borderRadius: 999,
            fontSize: 13,
            lineHeight: 1.4,
            background: "var(--admin-muted, rgba(120,120,140,0.12))",
            color: "var(--admin-foreground)",
            border: "1px solid var(--admin-border, rgba(120,120,140,0.2))",
          }}
        >
          {label}
        </span>
      ))}
    </div>
  )

  return (
    <Popover content={content} title={title} trigger="click" placement="bottomLeft">
      <button
        type="button"
        onClick={e => e.stopPropagation()}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "1px 8px 1px 3px",
          borderRadius: 6,
          cursor: "pointer",
          background: "transparent",
          border: "1px solid var(--admin-border, rgba(120,120,140,0.25))",
          color: "var(--admin-foreground)",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 20,
            height: 20,
            padding: "0 5px",
            borderRadius: 4,
            background: "var(--admin-muted, rgba(120,120,140,0.16))",
            color: "var(--admin-foreground)",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {count}
        </span>
        <span style={{ color: "var(--admin-muted-fg)" }}>{nounLabel}</span>
        <DownOutlined style={{ fontSize: 10, color: "var(--admin-muted-fg)" }} />
      </button>
    </Popover>
  )
}

export default CountListCell
